import { MarketingApi } from '../MarketingApi'
import { submitAndRoute } from '../concierge'
import {
  HubSpotEvent,
  Optional,
  Options,
  HubspotFormData,
  HubspotSubmitionValues,
  HubspotFormFields,
  HubspotFormFieldDataV4,
} from '../types'
import { isValidEmail, waitUntilFormExistsDeep } from '../utils'
import { debug } from '../logger'

const normalizeHubspotFormId = (id: string): string => String(id).replace('hsForm_', '')

const findEmailInput = (
  form: HTMLFormElement,
  emailFieldName?: string
): HTMLInputElement | undefined =>
  [emailFieldName, '0-1/email', 'email']
    .filter((name): name is string => Boolean(name))
    .map(name => form.querySelector<HTMLInputElement>(`input[name="${name}"]`))
    .find((input): input is HTMLInputElement => input !== null)

const getData = (submissionValues: HubspotSubmitionValues = {}) => {
  return Object.keys(submissionValues).reduce(
    (acc, key) => {
      if (Array.isArray(submissionValues[key])) {
        acc[key] = submissionValues[key].toString().replaceAll(',', ';')
      } else {
        acc[key] = submissionValues[key]
      }
      return acc
    },
    {} as Record<string, string>
  )
}

// https://help.chilipiper.com/hc/en-us/articles/360053798493-Setting-up-Concierge-with-HubSpot-Forms
// https://legacydocs.hubspot.com/global-form-events
export const deployHubspotIframe = (
  options: Optional<Options, 'form'>,
  forms?: NodeListOf<HTMLFormElement>
) => {
  const formIds = (options.formIds ?? []).map(id => normalizeHubspotFormId(String(id)))
  const marketingApi = new MarketingApi(options)
  let lead = {}

  const trackForm = (selector: string) =>
    waitUntilFormExistsDeep(selector, form => {
      if (marketingApi.form) {
        debug('Form already set - ignoring event')
        return
      }
      marketingApi.setForm(form)
    })

  // onFormReady is a one-shot message; if it fires before this snippet finishes
  // loading (deferred script, async tag manager), it's gone by the time we're
  // listening. Scan for an already-rendered form here too — whichever path
  // resolves first wins, the other short-circuits on marketingApi.form
  trackForm(
    (() => {
      if (options.formId) {
        return `form[data-form-id="${normalizeHubspotFormId(String(options.formId))}"]`
      }
      if (formIds.length > 0) {
        return formIds.map(id => `form[data-form-id="${id}"]`).join(', ')
      }
      return 'form[data-form-id]'
    })()
  )

  // Some Hubspot forms have form shortening enabled, so we need to wait for email to be typed in order to load the entire form
  // and then we can set the form to the marketingApi to make prelaunch work
  window.addEventListener('hs-form-event:on-ready', (event: Event) => {
    const form = window.HubspotFormsV4?.getFormFromEvent(event)
    if (
      !form ||
      (formIds.length > 0 && !formIds.includes(normalizeHubspotFormId(form.getFormId())))
    ) {
      return
    }
    const formElement = document.querySelector<HTMLFormElement>(
      `form[data-form-id="${form.getFormId()}"]`
    )
    if (formElement) {
      const emailInput = findEmailInput(formElement, marketingApi.prelaunch?.emailFieldName)
      if (emailInput) {
        const blurEventListener = () => {
          if (isValidEmail(emailInput.value)) {
            setTimeout(() => marketingApi.setForm(formElement), 800)
            debug('[CP] setForm attached to DOM element')
            emailInput.removeEventListener('blur', blurEventListener)
          } else {
            debug('email invalid')
          }
        }
        emailInput.addEventListener('blur', blurEventListener)
      }
    }
  })

  window.addEventListener('hs-form-event:on-submission:success', (event: Event) => {
    const form = window.HubspotFormsV4?.getFormFromEvent(event)
    if (
      !form ||
      (formIds.length > 0 && !formIds.includes(normalizeHubspotFormId(form.getFormId())))
    ) {
      return
    }

    const onLoadFormValues = (values: HubspotFormFieldDataV4[]) => {
      lead = values.reduce(
        (acc, { name, value }) => {
          if (Array.isArray(value)) {
            value = value.join(';')
          }
          acc[name] = value
          return acc
        },
        {} as Record<string, string | number>
      )

      // eslint-disable-next-line no-console
      console.log(lead)
      submitAndRoute(
        {
          ...options,
          map: true,
          lead: {
            ...lead,
            ...(options.lead ?? {}),
          },
        },
        marketingApi,
        forms
      )
    }

    form
      .getFormFieldValues()
      .then(onLoadFormValues)
      .catch(err => {
        console.error('Error loading form values from Hubspot', err)
      })
  })

  window.addEventListener('message', (event: HubSpotEvent) => {
    if (formIds.length > 0 && !formIds.includes(normalizeHubspotFormId(String(event.data.id)))) {
      return
    }
    if (
      ['hsFormCallback', 'hsCallsToActionCallback'].includes(event.data.type) &&
      ['onFormSubmitted', 'onCallToActionFormSubmitted'].includes(event.data.eventName)
    ) {
      if (Object.keys(lead).length === 0) {
        lead = getData((event.data.data as HubspotFormFields).submissionValues)
      }

      // eslint-disable-next-line no-console
      console.log(lead)
      submitAndRoute(
        {
          ...options,
          map: true,
          lead: {
            ...lead,
            ...(options.lead ?? {}),
          },
        },
        marketingApi,
        forms
      )
    }
    if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onBeforeFormSubmit') {
      lead = ((event.data.data as HubspotFormData[]) || []).reduce(
        (acc, data) => {
          if (Array.isArray(data.value)) {
            acc[data.name] = data.value.toString().replaceAll(',', ';')
          } else {
            acc[data.name] = data.value
          }
          return acc
        },
        {} as Record<string, string>
      )
      // eslint-disable-next-line no-console
      console.log('onBeforeFormSubmit', lead)
    }

    if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
      const formSelector = options.formId
        ? `form[data-form-id="${normalizeHubspotFormId(options.formId)}"]`
        : `form[data-form-id="${normalizeHubspotFormId(String(event.data.id))}"]`
      trackForm(formSelector)
    }
  })
}

// https://help.chilipiper.com/hc/en-us/articles/15550017287315-Setting-up-Concierge-with-HubSpot-Pop-Up-Forms
export const deployHubspotPopup = (options: Optional<Options, 'form'>) => {
  const marketingApi = new MarketingApi(options)
  window.addEventListener('message', function (event) {
    if (event.data.conversionId) {
      const lead = {} as Record<string, string>
      const fields = (document.querySelectorAll(
        'form[data-form-id="' + normalizeHubspotFormId(options.formId ?? '') + '"] input,select'
      ) ?? []) as unknown as HTMLInputElement[]
      for (let i = 0; i < fields.length; i++) {
        lead[fields[i].name] = fields[i].value
      }
      submitAndRoute(
        {
          ...options,
          map: true,
          lead: {
            ...lead,
            ...(options.lead ?? {}),
          },
        },
        marketingApi
      )
    }
  })
}
